import { z } from 'zod';
import type { ChatMessage } from '../components/ChatBubble';
import { wipePersistedQueryCache } from './persisterStore';

/**
 * Typed localStorage wrapper.
 * - Schema-validates on read; bad data is treated as missing
 * - Silently no-ops if storage is unavailable (private mode, quota, etc.)
 * - Namespaced keys to avoid collisions with other apps on the same origin
 */
const NAMESPACE = 'ti';

function key(name: string): string {
  return `${NAMESPACE}:${name}`;
}

function isAvailable(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const k = '__ti_probe__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

const available = isAvailable();

export function readValue<T>(name: string, schema: z.ZodType<T>): T | null {
  if (!available) return null;
  try {
    const raw = window.localStorage.getItem(key(name));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    const result = schema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function writeValue<T>(name: string, value: T): void {
  if (!available) return;
  try {
    window.localStorage.setItem(key(name), JSON.stringify(value));
  } catch {
    // Quota exceeded or similar — fail silently
  }
}

export function removeValue(name: string): void {
  if (!available) return;
  try {
    window.localStorage.removeItem(key(name));
  } catch {
    // ignore
  }
}

/**
 * Clear every key in the `ti:` namespace AND wipe the IndexedDB
 * persister store. Called on sign-out to ensure no app-level state
 * survives the session (personalPage, chat history, starter-dismissal
 * flag, memory-offer flag, pendingRef attribution, persisted TanStack
 * Query cache, etc.).
 *
 * Build Standard §3 Layer 4: signing out must wipe the client-side
 * storage namespace alongside the auth session. The Clerk session is
 * cleared separately by `signOut()` from `useClerk()`.
 *
 * Two storage layers, two wipes:
 *
 *   1. localStorage `ti:*` namespace — synchronous. Sweeps every
 *      app-level key (route hint, chat history, attribution, UX
 *      flags). Collect-then-delete pattern because modifying
 *      localStorage during iteration is unsafe (indices shift).
 *
 *   2. IndexedDB persister store (Batch 2 Part B, 12 Jun 2026) —
 *      fire-and-forget async. Wipes the dehydrated TanStack Query
 *      cache so account A's persisted chat history doesn't sit on
 *      disk after they hand the device to account B.
 *
 * Why the IDB wipe is fire-and-forget (not awaited):
 *   The race window — sign-out's `signOut() → navigate()` chain
 *   completes before this wipe finishes — is structurally harmless
 *   because Part A (11 Jun 2026) keys every persisted query by userId
 *   (`['chat', 'history', userId]`) AND the dehydrate filter in
 *   `main.tsx` rejects any query whose userId segment is null/empty.
 *   Account B literally cannot read account A's persisted entries by
 *   key. This wipe is defence-in-depth (preventing unbounded IDB
 *   growth, clean factory-reset semantics), not load-bearing for
 *   isolation. Keeping the function signature synchronous means every
 *   existing AND future sign-out path is automatically covered with
 *   no caller changes — no "developer forgot to await both" footgun.
 *
 * Per-user chat history keys (10 Jun 2026 structural fix) written as
 * `ti:chatHistory:{userId}` share the `ti:` prefix and are swept by
 * the wholesale localStorage clear alongside every other app-state
 * key. Without that, namespaced chat history would accumulate
 * indefinitely on shared devices as new accounts sign in.
 */
export function clearAllAppStorage(): void {
  // IndexedDB wipe first — runs regardless of localStorage availability.
  // Fire-and-forget; see JSDoc above and `wipePersistedQueryCache` for
  // why the race is structurally harmless.
  void wipePersistedQueryCache();

  if (!available) return;
  try {
    const prefix = `${NAMESPACE}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k !== null && k.startsWith(prefix)) {
        keysToRemove.push(k);
      }
    }
    for (const k of keysToRemove) {
      window.localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

// ─── Personal page — route hint only ─────────────────────────────────────

/**
 * `personalPage` is a thin route-hint stored in localStorage. It tells
 * `RootRoute` whether the user has at least joined a town (HomePage vs
 * /me decision) and tells `RequireAuth` whether they're mid-onboarding
 * (no Clerk session yet but a town has been redeemed).
 *
 * It is NOT the source of truth for any user data. Identity + profile
 * live in the API and are read via `useUser()` (GET /v1/users/me) from
 * Batch 25c-2 onward.
 *
 * Schema fields:
 *   - `token` — historical synonym for `town`; both are the lowercase
 *     town slug. Used by /memories/:token for keepsake-page validation.
 *   - `town` — the town slug.
 *   - `joinedAt` — ISO timestamp of user creation; sourced from the API
 *     response on /invitation/complete or /me. Cosmetic; the API value
 *     is authoritative.
 *
 * Legacy entries written before Batch 25c-2 may include a `profile`
 * field. Zod's default object behaviour strips unknown keys on read, so
 * those entries parse cleanly with the profile silently dropped. Profile
 * data has already been written to the API for any user who has
 * completed onboarding post-25c-2; pre-25c-2 users re-onboard.
 */
const personalPageSchema = z.object({
  token: z.string().min(1),
  town: z.string().min(1),
  joinedAt: z.string().datetime(),
});

export type PersonalPage = z.infer<typeof personalPageSchema>;

export function readPersonalPage(): PersonalPage | null {
  return readValue('personalPage', personalPageSchema);
}

export function writePersonalPage(page: PersonalPage): void {
  writeValue('personalPage', page);
}

export function clearPersonalPage(): void {
  removeValue('personalPage');
}

// ─── Chat history persistence ────────────────────────────────────────────

/**
 * Message kind taxonomy. Captured at construction site, persisted with the
 * message, and used downstream for:
 *   - Memory Page enrichment (e.g. extract `pick-ack` messages, group by
 *     `dateLocal` for a day-by-day timeline)
 *   - Future analytics (which intents fire most, conversion rates)
 *   - LLM-era classification when the API ships
 *
 * 'memory-offer' — client-side single-fire nudge near stay end pointing
 * the user at their Memory Page. Doesn't come from sendMessage; the
 * trigger lives in MePage and writes the message directly. Distinct
 * kind so it segments cleanly in future analytics (one-time UX prompt,
 * not a chat reply).
 *
 * 'persona-offline-faq' — client-only saved-answer bubble surfaced by
 * the offline FAQ fallback (Phase 6 sub-commit 6b, 13 Jun 2026). When
 * shouldSend is false and the guest's query fuzzy-matches an FAQ in
 * the cached catalogue above the matcher's confidence threshold,
 * handleSend writes this kind instead of routing through the outbox.
 * NEVER appears server-side — the user message paired with it is
 * deliberately not enqueued (one answer per question; queueing would
 * produce a double-answer when the user returns online). Renders with
 * a visually distinct ChatBubble variant (dashed border + "Saved info"
 * meta-row label) so the guest can never confuse a cached answer for
 * a live persona reply. Not in SERVER_OWNED_CLIENT_KINDS over in
 * lib/api/chatHistory.ts, so reconcileHistory passes it through
 * unchanged on every server sync — exchanges persist locally for the
 * 7-day history window, then expire with everything else.
 *
 * 'seed' covers the persona's static intro lines — those don't actually
 * persist (filtered out in MePage by id), but the kind stays in the enum
 * so seed-resolved ChatMessages satisfy the type without a separate
 * "in-memory only" variant.
 */
export const messageKindSchema = z.enum([
  'user',
  'onboarding',
  'persona-reply',
  'weather',
  'teatime',
  'pick-ack',
  'profile-update',
  'memory-offer',
  'persona-offline-faq',
  'seed',
]);

export type MessageKind = z.infer<typeof messageKindSchema>;

/**
 * Persisted shape v1 — legacy, no `dateLocal` or `kind`. Retained so v2
 * code can still read entries written by pre-batch-22b deploys and migrate
 * them on the fly. Once the active 7-day window has rolled over, no real
 * v1 entries should remain on any device.
 */
const persistedChatMessageV1Schema = z.object({
  id: z.string().min(1),
  from: z.enum(['persona', 'user']),
  text: z.string().min(1),
  timestamp: z.string().min(1),
});

type PersistedChatMessageV1 = z.infer<typeof persistedChatMessageV1Schema>;

/**
 * Persisted shape v2 — current. Adds `dateLocal` (YYYY-MM-DD via local
 * components, NOT UTC slice — see `isoDateLocal` in onboarding.ts) and
 * `kind` for downstream grouping and filtering.
 *
 * `pickName` (optional) — populated by `sendMessage`'s pick-ack path
 * with the venue name. Lets Memory Page enrichment recover "places you
 * went" without parsing the persona's ack text (fragile to voice tweaks).
 * Optional rather than discriminated-union'd by `kind` to keep the schema
 * additive — any future intent that wants to attach metadata can add its
 * own optional field without re-shaping the rest. Adding an optional
 * field is backwards-compatible with already-persisted v2 entries, so no
 * version bump is needed.
 *
 * Drops vs runtime ChatMessage:
 *   - attachments: blob: URLs from URL.createObjectURL don't survive a
 *     reload. Text content of attachment-bearing messages still persists;
 *     attachment slot degrades to absent. (When R2 ships, attachments
 *     persist via durable key references.)
 *   - status: transient ('sending' / 'error') — re-derived as 'sent' on read.
 *   - uploadProgress: transient.
 */
const persistedChatMessageV2Schema = z.object({
  id: z.string().min(1),
  from: z.enum(['persona', 'user']),
  text: z.string().min(1),
  timestamp: z.string().min(1),
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  kind: messageKindSchema,
  pickName: z.string().min(1).optional(),
});

type PersistedChatMessageV2 = z.infer<typeof persistedChatMessageV2Schema>;

const chatHistoryV1Schema = z.object({
  version: z.literal(1),
  messages: z.array(persistedChatMessageV1Schema),
  updatedAt: z.string().datetime(),
});

const chatHistoryV2Schema = z.object({
  version: z.literal(2),
  messages: z.array(persistedChatMessageV2Schema),
  updatedAt: z.string().datetime(),
});

/**
 * Union covers both versions on read. Order matters for Zod — v2 first so
 * v2-shaped entries match the strict v2 branch before falling through.
 * New writes always go out as v2 (see `writeChatHistory`).
 */
const chatHistorySchema = z.union([chatHistoryV2Schema, chatHistoryV1Schema]);

const MAX_HISTORY_MESSAGES = 50;
const HISTORY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * YYYY-MM-DD using local components. Defined inline rather than imported
 * from `onboarding.ts` (`isoDateLocal`) or `teatime.ts` (`getLocalDateString`)
 * to keep storage as a foundational module without lateral imports.
 */
function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Compose the per-user chat history key name (10 Jun 2026 structural
 * fix). Returns null when no userId is supplied so the caller can
 * silently no-op rather than fall through to a shared key — the whole
 * point of the structural fix is that there is NO shared key from
 * which a cross-account read could occur.
 *
 * The returned name is consumed by `readValue` / `writeValue` /
 * `removeValue`, which prepend the `ti:` namespace, producing the
 * final localStorage key `ti:chatHistory:{userId}`. Each Clerk user
 * has their own key; account A and account B on the same device
 * literally cannot read each other's chat history because they look
 * at different keys.
 *
 * Why null-on-missing rather than a fallback to `'chatHistory'`
 * (the pre-fix unscoped key): falling back to a shared key would
 * reintroduce the original bleed — if any caller forgot to pass
 * userId, both accounts would converge on the same key. Returning
 * null forces the reader/writer paths to silently abort, which is
 * visible as "chat doesn't persist for this user" — a self-detecting
 * bug rather than a silent identity leak.
 *
 * The `userId` parameter is `string | null | undefined` (rather than
 * just `string`) so the function accepts Clerk's `useAuth().userId`
 * shape directly, which is `string | null | undefined` during the
 * loading state. No coercion needed at call sites.
 */
function chatHistoryKeyName(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `chatHistory:${userId}`;
}

/**
 * Infer message kind from id prefix. Used during v1 → v2 migration only.
 *
 * Lossy: legacy `srv-p-*` ids (everything sendMessage produced — weather,
 * teatime, pick-ack, canned) all collapse to 'persona-reply' since v1
 * didn't preserve which intent path served them. Acceptable: only matters
 * for the active 7-day window of pre-migration history; new v2 entries
 * carry kind set at write time, including the kind returned from
 * `sendMessage` for persona replies.
 */
function inferLegacyKind(
  id: string,
  from: 'persona' | 'user',
): MessageKind {
  if (id.startsWith('onboard-')) return 'onboarding';
  if (id.startsWith('profile-update-')) return 'profile-update';
  if (id.startsWith('teatime-')) return 'teatime';
  if (id.startsWith('memory-offer-')) return 'memory-offer';
  if (id.startsWith('u-')) return 'user';
  if (id.startsWith('p-err-')) return 'persona-reply';
  if (id.startsWith('srv-p-')) return 'persona-reply';
  return from === 'user' ? 'user' : 'persona-reply';
}

function migrateV1ToV2(
  v1: ReadonlyArray<PersistedChatMessageV1>,
  todayLocal: string,
): PersistedChatMessageV2[] {
  return v1.map((m) => ({
    ...m,
    // Legacy entries don't carry a date — best-effort use today. Wrong
    // for messages from earlier in the week, but Memory Page enrichment
    // (the consumer of this field) is a future feature; the active week
    // of history that 7-day expiry preserves is approximately today
    // anyway. Once everyone has rolled over to v2 writes, this branch
    // is dead code.
    dateLocal: todayLocal,
    kind: inferLegacyKind(m.id, m.from),
    // Legacy entries can't recover pickName (it wasn't captured in v1).
    // Pick-ack text still exists; the venue name lives only in the ack
    // string's first chunk. Memory Page enrichment for legacy weeks will
    // simply lack the "places you went" list — acceptable given the
    // 7-day window means this affects very few real users.
  }));
}

/**
 * Read chat history for a specific Clerk user. Returns ChatMessage[]
 * (status defaulted to 'sent') or null if no history, expired,
 * invalid, OR no userId was supplied. Migrates v1 entries to v2 in
 * memory; the migration is persisted on the next write.
 *
 * userId scoping (10 Jun 2026 structural fix): the storage key is
 * `ti:chatHistory:{userId}`, scoped by Clerk identity. Account A and
 * account B on the same device literally cannot read each other's
 * chat history — the reads go to different keys. This makes the
 * cross-account chat-body bleed (where account B inherited account
 * A's messages after a sign-out / sign-in cycle on the same browser)
 * structurally impossible. The `clearAllAppStorage()` call on
 * sign-out remains as defence-in-depth hygiene (preventing
 * unbounded growth on shared devices) but is no longer load-bearing
 * for isolation.
 *
 * userId is OPTIONAL during the migration window — when omitted,
 * the function silently returns null rather than falling back to a
 * shared key. This is deliberate: a fallback to the legacy
 * unscoped `ti:chatHistory` key would reintroduce the very bug
 * we're fixing if any future caller forgot to pass userId. The
 * null return is visible as "chat doesn't persist for this user"
 * — a self-detecting bug, not a silent identity leak. After all
 * callers are confirmed to pass userId, the parameter can be made
 * required in a follow-up commit.
 *
 * One-time UX cost for already-deployed users: the legacy
 * `ti:chatHistory` key from before this commit is now orphaned —
 * no caller reads it. Existing users will see a brief flash of
 * empty chat on the first /me render after deploy, until the
 * server-side chat history reconciliation (via `useChatHistory`)
 * repopulates messages from the API canonical. The orphaned
 * legacy key is swept by `clearAllAppStorage()` on the next
 * sign-out.
 *
 * Expiry: any history older than 7 days is treated as gone and
 * cleared from storage. Active users' visits keep refreshing
 * updatedAt (via writeChatHistory on every message change), so an
 * active week-long stay never loses history. A returning visitor a
 * year later sees a clean slate, not their last holiday's chat.
 */
export function readChatHistory(
  userId?: string | null,
): ChatMessage[] | null {
  const name = chatHistoryKeyName(userId);
  if (!name) return null;

  const raw = readValue(name, chatHistorySchema);
  if (!raw) return null;

  const updatedMs = Date.parse(raw.updatedAt);
  if (Number.isNaN(updatedMs) || Date.now() - updatedMs > HISTORY_MAX_AGE_MS) {
    removeValue(name);
    return null;
  }

  if (raw.messages.length === 0) return null;

  // Normalise to v2 shape in-memory.
  const v2Messages: PersistedChatMessageV2[] =
    raw.version === 2
      ? raw.messages
      : migrateV1ToV2(raw.messages, todayLocalDate());

  // Defensive cap on read in case a future writer raised the cap and a
  // user downgraded; never feed more than MAX_HISTORY_MESSAGES into the page.
  const capped = v2Messages.slice(-MAX_HISTORY_MESSAGES);

  return capped.map((m) => ({
    id: m.id,
    from: m.from,
    text: m.text,
    timestamp: m.timestamp,
    dateLocal: m.dateLocal,
    kind: m.kind,
    // Spread-conditional for exactOptionalPropertyTypes — only include
    // pickName if it's actually present.
    ...(m.pickName !== undefined ? { pickName: m.pickName } : {}),
    status: 'sent' as const,
  }));
}

/**
 * Persist chat history for a specific Clerk user. Filters out
 * empty-text messages (would render as an empty bubble after we strip
 * attachments, since attachment-only messages have no text). Caps to
 * the most recent MAX_HISTORY_MESSAGES. Idempotent — safe to call on
 * every messages-array change.
 *
 * Writes as v2. Reads accept both v1 and v2 (see `chatHistorySchema`),
 * so a deploy-and-revert cycle won't drop active-week history.
 *
 * userId scoping: the write targets `ti:chatHistory:{userId}`, the
 * same per-user key `readChatHistory` reads from. When userId is
 * omitted (transitional state during the structural-fix rollout)
 * the function silently no-ops rather than writing to a shared key
 * — see the rationale on `chatHistoryKeyName` and `readChatHistory`.
 */
export function writeChatHistory(
  messages: ReadonlyArray<ChatMessage>,
  userId?: string | null,
): void {
  const name = chatHistoryKeyName(userId);
  if (!name) return;

  const persisted: PersistedChatMessageV2[] = messages
    .filter((m) => m.text.trim().length > 0)
    .slice(-MAX_HISTORY_MESSAGES)
    .map((m) => ({
      id: m.id,
      from: m.from,
      text: m.text,
      timestamp: m.timestamp,
      dateLocal: m.dateLocal,
      kind: m.kind,
      ...(m.pickName !== undefined ? { pickName: m.pickName } : {}),
    }));

  writeValue(name, {
    version: 2 as const,
    messages: persisted,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Clear chat history for a specific Clerk user. No-op when userId is
 * omitted — same null-on-missing discipline as `readChatHistory` and
 * `writeChatHistory`, so accidental clears of a shared key are
 * impossible.
 */
export function clearChatHistory(userId?: string | null): void {
  const name = chatHistoryKeyName(userId);
  if (!name) return;
  removeValue(name);
}

// ─── Memory Page offer flag ──────────────────────────────────────────────

/**
 * Tracks whether the persona has already offered the user their Memory
 * Page near the end of their stay. Keyed by `forStayEnd` so a returning
 * user with a NEW stay (different end date) gets a fresh offer rather
 * than one persisted from a prior trip.
 *
 * Single-fire by design: the offer fires once on the day-before-last
 * or last day of the stay, then this flag pins which stayEnd it was
 * fired for. Reloads within the same stay see the matching flag and
 * stay silent. Sign-out clears `ti:*` wholesale and resets the offer.
 *
 * Why a date string rather than a boolean: a boolean would re-fire on
 * every subsequent stay because we'd have no way to distinguish "fired
 * for this stay" from "fired for some previous stay". Tying the flag
 * to a specific stayEnd makes the re-fire trigger natural.
 */
const memoryOfferedSchema = z.object({
  forStayEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type MemoryOffered = z.infer<typeof memoryOfferedSchema>;

export function readMemoryOffered(): MemoryOffered | null {
  return readValue('memoryOffered', memoryOfferedSchema);
}

export function setMemoryOffered(value: MemoryOffered): void {
  writeValue('memoryOffered', value);
}

export function clearMemoryOffered(): void {
  removeValue('memoryOffered');
}

// ─── Starter chips dismissal flag ────────────────────────────────────────

/**
 * Persistent flag — once the user has sent their first message (typed or
 * chip-tap), starter chips never reappear. Without this, every reload
 * would re-show the chips even for a user who has been chatting all week.
 */
const startersDismissedSchema = z.object({
  dismissedAt: z.string().datetime(),
});

export function readStartersDismissed(): boolean {
  return readValue('startersDismissed', startersDismissedSchema) !== null;
}

export function setStartersDismissed(): void {
  writeValue('startersDismissed', { dismissedAt: new Date().toISOString() });
}

export function clearStartersDismissed(): void {
  removeValue('startersDismissed');
}

// ─── Pending partner ref (attribution) ───────────────────────────────────

/**
 * `pendingRef` is the partner slug captured from a `?ref=...` query
 * parameter on a `/join/:town` URL (a partner-specific QR code). It
 * survives the Clerk magic-link sign-up roundtrip — which takes the
 * user out of the app and back via their email client — so that
 * POST /v1/users/invitation/complete can include the ref in its request
 * body, and the API can write `users.partner_ref` against the new row.
 *
 * Lifecycle:
 *   - WRITTEN by JoinPage when `?ref=...` is present on the join URL.
 *   - READ by OnboardingCompletePage immediately before calling
 *     completeOnboarding.
 *   - CLEARED by OnboardingCompletePage on a SUCCESSFUL complete only.
 *     A transient network failure preserves the ref for retry: the API
 *     endpoint is idempotent, and partner_ref is first-touch (never
 *     rewritten on retry), so a retry with the same ref is harmless.
 *     Clearing too early would lose attribution to a flaky connection.
 *   - SWEPT by clearAllAppStorage on sign-out as a safety net — a later
 *     guest signing up on the same device must never inherit attribution
 *     from a previous occupant.
 *
 * Validation is deliberately loose (no slug-format regex) to mirror the
 * API: an unknown or inactive ref resolves to NULL server-side, not a
 * rejection. The length bound `min(1).max(64)` matches the API's
 * `CompleteSchema.ref` bound exactly — anything beyond that is dropped
 * silently at JoinPage rather than persisted.
 *
 * Separate key (not folded into `personalPage`) because:
 *   - Different lifecycle: pendingRef clears on successful complete,
 *     personalPage lives until sign-out.
 *   - personalPage is a route hint; attribution data doesn't belong in it.
 *   - One purpose per key matches the existing pattern of `memoryOffered`
 *     and `startersDismissed`.
 */
const pendingRefSchema = z.object({
  ref: z.string().min(1).max(64),
});

export type PendingRef = z.infer<typeof pendingRefSchema>;

export function readPendingRef(): PendingRef | null {
  return readValue('pendingRef', pendingRefSchema);
}

export function writePendingRef(value: PendingRef): void {
  writeValue('pendingRef', value);
}

export function clearPendingRef(): void {
  removeValue('pendingRef');
}
